import SPInspection from '@lib/spproject/SPInspection';
import type { Inspection } from '@lib/spproject/types';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Loads the snapshot images embedded in an inspection's (.spproject) file on demand.
 *
 * Snapshots are just the inspection's image resources, so they are read through the existing
 * `getResources()` and filtered with `SPInspection.isSnapshot`.
 *
 * The file is only downloaded when `load()` is called (e.g. the user clicks the snapshots
 * button), never on render/expand. Whether an inspection has snapshots — and how many — is
 * already known up front from the inspection's `resources` metadata embedded in the list, so
 * the file is only fetched when the user actually wants to view the photos. Subsequent calls
 * are cached.
 *
 * The listing already withholds inspections the caller cannot read, and the file endpoint
 * re-checks `inspection:get` against the author, so no extra permission gate is needed here.
 */
export function useInspectionSnapshots(
  inspectionId: string,
  loadInspection: (inspectionId: string) => Promise<SPInspection>,
) {
  const [snapshots, setSnapshots] = useState<Inspection.NoteResource[]>([]);
  const [loading, setLoading] = useState(false);

  const loadedRef = useRef(false);
  const inFlightRef = useRef<Promise<void> | null>(null);
  const mountedRef = useRef(true);
  const urlsRef = useRef<string[]>([]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      urlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  const load = useCallback(async (): Promise<void> => {
    if (loadedRef.current) return;
    if (inFlightRef.current) return inFlightRef.current;

    setLoading(true);

    const task = (async () => {
      try {
        const sp = await loadInspection(inspectionId);
        const result = await sp.getResources(SPInspection.isSnapshot);

        if (!mountedRef.current) {
          result.forEach((snapshot) => snapshot.resourceUrl && URL.revokeObjectURL(snapshot.resourceUrl));
          return;
        }

        urlsRef.current = result.map((snapshot) => snapshot.resourceUrl).filter(Boolean);
        loadedRef.current = true;
        setSnapshots(result);
      } catch (error) {
        console.error('Failed to load snapshots:', { inspectionId, error });
      } finally {
        if (mountedRef.current) setLoading(false);
        inFlightRef.current = null;
      }
    })();

    inFlightRef.current = task;
    return task;
  }, [inspectionId, loadInspection]);

  return { snapshots, loading, load };
}
